import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from 'react';
import classNames from 'classnames';
import { cloneDeep, forEachRight, range } from 'lodash';
import {
  CreditRatingMax,
  FinanceType,
} from '@upstart/harmony/lib/legos/constants';
import { CreditTier } from '@upstart/harmony/lib/legos/creditPricingType';
import { DealerLaunchStepProps } from '../types';
import { useLaunchpadForm } from '../../../hooks/useLaunchpadForm';
import { DealerLaunchFormCardLayout } from '../DealerLaunchFormCardLayout';
import { RangeSlider } from '../../Input/Slider/RangeSlider';
import styles from '../styles.module.scss';
import { Button } from '../../Input/Button/Button';
import closeImage from '../../../assets/close.png';
import { DEFAULT_CREDIT_TIERS } from '../defaults';

const CREDIT_SCORE_MIN = 1;

const DEBOUNCE_TIME_MS = 2000;

type FormData = {
  creditTiers: { value: number }[];
};

const DealerLaunchPricingCredit: FunctionComponent<
  DealerLaunchStepProps & {
    financeType: FinanceType.LEASE | FinanceType.PURCHASE;
  }
> = ({ financeType, dealer, prevStep, nextStep }) => {
  const form = useLaunchpadForm({
    defaultValues: {
      creditTiers: DEFAULT_CREDIT_TIERS,
    },
  });
  const savedTiers =
    dealer.launchpad?.creditPricing?.[financeType]?.creditTiers;

  const onSubmit = async (data: FormData) => {
    const creditTiers = data.creditTiers.map(({ value }, idx) => ({
      key: `tier${idx + 1}`,
      name: `Tier${idx + 1}`,
      value,
    }));
    // overwrite all the apr configs for different client types and conditions
    await nextStep({
      launchpad: {
        creditPricing: {
          [financeType]: {
            creditTiers,
          },
        },
      },
    });
  };

  const [isDisabled, setDisabled] = useState(false);

  const handleDebounce = useCallback(
    (type: 'start' | 'end') => {
      setDisabled(type === 'start');
    },
    [setDisabled],
  );

  const { setValue, register } = form;
  const fields = form.watch('creditTiers');
  useEffect(() => {
    register('creditTiers');
  }, [register]);
  useEffect(() => {
    if (savedTiers) {
      setValue('creditTiers', savedTiers);
    }
  }, [savedTiers, setValue]);

  const getClampedValue = useCallback(
    (
      value: number,
      clonedFields: CreditTier[],
      idx: number,
      ignoreLower?: boolean,
    ) => {
      const prevValue = clonedFields[idx - 1]?.value;
      const nextValue = clonedFields[idx + 1]?.value;
      if (prevValue && value + 1 >= prevValue) {
        return prevValue - 2;
      } else if (!ignoreLower && nextValue && value - 1 <= nextValue) {
        return nextValue + 2;
      } else if (value <= 0) {
        return 1;
      } else if (value >= CreditRatingMax) {
        return CreditRatingMax - 1;
      }
      return value;
    },
    [],
  );
  const clampValues = useCallback(
    (clonedFields: CreditTier[]) => {
      clonedFields.forEach((field, idx) => {
        // eslint-disable-next-line no-param-reassign
        field.value = getClampedValue(field.value, clonedFields, idx, true);
      });
      forEachRight(clonedFields, (field, idx) => {
        // eslint-disable-next-line no-param-reassign
        field.value = getClampedValue(field.value, clonedFields, idx);
      });
    },
    [getClampedValue],
  );

  const addCreditTier = () => {
    const clonedFields = cloneDeep(fields);
    let lowerbound = clonedFields[clonedFields.length - 1].value - 50;
    lowerbound = lowerbound > CREDIT_SCORE_MIN ? lowerbound : CREDIT_SCORE_MIN;
    const newKey = Math.random().toString();
    clonedFields.push({
      name: newKey,
      key: newKey,
      value: lowerbound,
    });
    clampValues(clonedFields);
    setValue('creditTiers', clonedFields);
  };
  const removeCreditTier = (idx: number) => {
    setValue('creditTiers', [
      ...fields.slice(0, idx),
      ...fields.slice(idx + 1, fields.length),
    ]);
  };

  const copyFromFinance = () => {
    const tiers = cloneDeep(
      dealer.launchpad?.creditPricing?.[FinanceType.PURCHASE]?.creditTiers ||
        [],
    );
    setValue('creditTiers', tiers);
  };
  const canDelete = fields.length > 4;
  const canAdd = fields.length < 9;

  /**
   * Handle changes to any of the range sliders
   */
  const onSliderChange = (
    fieldIdx: number,
    [newLowerBound, newUpperBound]: number[],
  ) => {
    // // force high excellent and low poor to be at the edges
    const clonedFields = cloneDeep(fields);
    const currentTier = clonedFields[fieldIdx];
    const previousTier = clonedFields[fieldIdx - 1];
    currentTier.value = getClampedValue(newLowerBound, clonedFields, fieldIdx);
    if (previousTier) {
      previousTier.value = getClampedValue(
        newUpperBound + 1,
        clonedFields,
        fieldIdx - 1,
      );
    }
    clampValues(clonedFields);
    form.setValue('creditTiers', clonedFields, {
      shouldDirty: true,
    });
  };

  const type = financeType === FinanceType.PURCHASE ? 'Finance' : 'Lease';
  return (
    <DealerLaunchFormCardLayout
      className={styles.creditTierStep}
      form={form}
      disabled={isDisabled || !fields?.length}
      title="Which Credit Tiers Do You Use Online?"
      prevStep={prevStep}
      onFormSubmit={onSubmit}
    >
      <div className="flex-row">
        <h1>Set your online credit tiers for {type} Purchases</h1>
        <div className="spacer" />
        {financeType === FinanceType.LEASE && (
          <Button variant="minimal" onClick={copyFromFinance}>
            Copy Tiers From finance
          </Button>
        )}
      </div>
      {fields.map((field, idx) => {
        const high = idx === 0 ? CreditRatingMax : fields[idx - 1].value - 1;
        return (
          <div style={{ width: '100%' }} key={field.name + fields.length}>
            <RangeSlider
              debounceTimeMs={DEBOUNCE_TIME_MS}
              onDebounce={handleDebounce}
              value={[field.value, high]}
              className={classNames(
                styles.creditTierSlider,
                idx === fields.length - 1 ? styles.last : '',
              )}
              label={`Tier${idx + 1}`}
              max={CreditRatingMax + 2}
              min={CREDIT_SCORE_MIN}
              step={null}
              marks={range(CREDIT_SCORE_MIN, CreditRatingMax + 1, 25).reduce(
                (p, c) => ({ ...p, [c]: <></> }),
                {},
              )}
              onChange={(newValues) => {
                onSliderChange(idx, newValues);
              }}
            />
            {canDelete && (
              <Button
                title="Remove Tier"
                variant="minimal"
                className={classNames(styles.deleteButton)}
                onClick={() => removeCreditTier(idx)}
              >
                <img height={14} src={closeImage} alt="Delete" />
              </Button>
            )}
          </div>
        );
      })}
      {canAdd && !!fields.length && (
        <Button className={styles.addButton} onClick={addCreditTier}>
          <div>+ Add Another Credit Tier</div>
        </Button>
      )}
    </DealerLaunchFormCardLayout>
  );
};

export const DealerLaunchFinancePricingCredit: FunctionComponent<DealerLaunchStepProps> = (
  props,
) => (
  <DealerLaunchPricingCredit {...props} financeType={FinanceType.PURCHASE} />
);

export const DealerLaunchLeasePricingCredit: FunctionComponent<DealerLaunchStepProps> = (
  props,
) => <DealerLaunchPricingCredit {...props} financeType={FinanceType.LEASE} />;
